import { Box, Divider, Icon, Stack } from "@mui/material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { routes } from "src/shared/routes";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "../hooks/useSSTheme";
import { useRouterExt } from "../hooks/useRouterExt";
import { useTeamConfig } from "./TeamConfigProvider";

export function NavMenu() {
  const theme = useSSTheme();
  const router = useRouterExt();
  const { TEAM_GLOBAL_SETTINGS, LANGUAGES } = useTeamConfig();
  const TeamAdditionalSubMenuItems = TEAM_GLOBAL_SETTINGS?.NAV_MENU?.ADDITIONAL_SUB_MENU_ITEMS; // prettier-ignore
  const upcomingEventsLabel = LANGUAGES?.NAV_MENU?.UPCOMING_EVENTS || "Upcoming Events"; // prettier-ignore

  const mainMenuItems = [
    {
      id: 1,
      label: upcomingEventsLabel,
      icon: <ConfirmationNumberOutlinedIcon />,
      route: routes.home,
      isHidden: TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_UPCOMING_EVENTS,
    },
    {
      id: 2,
      label: "Earn Rewards",
      icon: <EmojiEventsOutlinedIcon />,
      route: routes.my_rewards,
      isHidden: TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_EARN_REWARDS,
    },
    {
      id: 3,
      label: "My Group",
      icon: <Groups2OutlinedIcon />,
      route: routes.my_group,
      isHidden: TEAM_GLOBAL_SETTINGS?.DISABLE_SIT_WITH_FRIENDS,
    },
    {
      id: 4,
      label: "Marketplace",
      icon: <ShoppingBagOutlinedIcon sx={{ marginTop: "-1px" }} />,
      route: routes.marketplace_home,
      isHidden: !TEAM_GLOBAL_SETTINGS?.IS_MARKETPLACE_ENABLED,
    },
  ];

  const subMenuItems = [
    {
      id: 1,
      label: LANGUAGES?.NAV_MENU?.MANAGE_SUBSCRIPTION || "Manage Subscription",
      route: routes.manage_subscription,
      isHidden: TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_MANAGE_SUBSCRIPTION,
    },

    {
      id: 2,
      label: "Purchase History",
      route: routes.purchase_history,
      isHidden: TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_PURCHASE_HISTORY,
    },
    {
      id: 3,
      label: "FAQs",
      route: routes.faqs,
      isHidden: !LANGUAGES?.FAQS || LANGUAGES?.FAQS?.length === 0 || TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_FAQS, // prettier-ignore
    },
  ];

  const onClickMenuItem = (route: string) => {
    router.pushSchema(route);
  };

  const visibleMainMenuItems = mainMenuItems.filter((itm) => !itm.isHidden);
  const visibleSubMenuItems = subMenuItems.filter((itm) => !itm.isHidden);
  // a team can hide every default sub item and still inject its own
  const hasSubMenu = visibleSubMenuItems.length > 0 || Boolean(TeamAdditionalSubMenuItems); // prettier-ignore

  // nothing left to show, so we skip the bordered container entirely
  if (!visibleMainMenuItems.length && !hasSubMenu) return null;

  return (
    <Stack
      border={`1px solid ${theme.colors.gray[100]}`}
      bgcolor={theme.colors.gray[50]}
      borderRadius={3}
    >
      {visibleMainMenuItems.length > 0 ? (
        <>
          <Stack spacing={1} p={2}>
            {visibleMainMenuItems.map((itm) => {
              const isActive = router.pathname === itm.route;

              return (
                <Stack
                  onClick={() => onClickMenuItem(itm.route)}
                  spacing={2}
                  direction={"row"}
                  alignItems={"center"}
                  key={itm.id}
                  p={1}
                  sx={{
                    cursor: isActive ? "not-allowed" : "pointer",
                    pointerEvents: isActive ? "none" : "auto",
                    borderRadius: 2,
                    "& .MuiSvgIcon-root": {
                      color: theme.colors.gray[200],
                    },
                    "&:hover": {
                      backgroundColor: theme.colors.gray[100],
                    },
                  }}
                >
                  <Stack direction={"row"} spacing={2}>
                    <Stack
                      sx={{
                        "& .MuiSvgIcon-root": {
                          color: `${
                            isActive
                              ? theme.colors.teamPrimary
                              : theme.colors.gray[200]
                          } !important`,
                        },
                      }}
                    >
                      {itm.icon}
                    </Stack>
                    <SSText
                      variant="h6"
                      fontWeight={600}
                      color={
                        isActive
                          ? theme.colors.teamPrimary
                          : theme.colors.gray[300]
                      }
                    >
                      {itm.label}
                    </SSText>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>

          {hasSubMenu ? (
            <Box px={1}>
              <Divider />
            </Box>
          ) : null}
        </>
      ) : null}

      {hasSubMenu ? (
        <Stack p={2}>
          {visibleSubMenuItems.map((itm) => {
            const isActive = router.pathname === itm.route;
            return (
              <Stack
                spacing={2}
                direction={"row"}
                alignItems={"center"}
                onClick={() => onClickMenuItem(itm.route)}
                key={itm.id}
                p={1}
                sx={{
                  cursor: isActive ? "not-allowed" : "pointer",
                  pointerEvents: isActive ? "none" : "auto",
                  borderRadius: 2,
                  "& .MuiSvgIcon-root": {
                    color: theme.colors.gray[200],
                  },
                  "&:hover": {
                    backgroundColor: theme.colors.gray[100],
                  },
                }}
              >
                <SSText
                  variant="body"
                  color={
                    isActive ? theme.colors.teamPrimary : theme.colors.gray[300]
                  }
                >
                  {itm.label}
                </SSText>
              </Stack>
            );
          })}

          {TeamAdditionalSubMenuItems ? <TeamAdditionalSubMenuItems /> : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
